import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, TrendingDown, AlertTriangle, Moon } from 'lucide-react';

interface SleepAnalysisSummaryProps {
  sleepScore: number;
  previousScore: number;
  insights: {
    type: 'success' | 'warning' | 'info';
    title: string;
    description: string;
    impact: string;
  }[];
}

export function SleepAnalysisSummary({ sleepScore, previousScore, insights }: SleepAnalysisSummaryProps) {
  const scoreDiff = sleepScore - previousScore;
  const defaultInsights = [
    {
      type: 'success' as const,
      title: 'Deep Sleep Quality',
      description: 'Your deep sleep duration of 1h 38m helps boost physical recovery and immune function.',
      impact: 'Enhances memory consolidation and muscle recovery'
    },
    {
      type: 'info' as const,
      title: 'Sleep Consistency',
      description: 'Your sleep schedule has been consistent, maintaining a regular circadian rhythm.',
      impact: 'Supports hormonal balance and cognitive function'
    },
    {
      type: 'warning' as const,
      title: 'REM Sleep Pattern',
      description: 'REM sleep was slightly below optimal range. Consider extending total sleep duration.',
      impact: 'Affects emotional regulation and learning'
    }
  ];

  const activeInsights = insights.length > 0 ? insights : defaultInsights;

  return (
    <div className="theme-card p-6 relative overflow-hidden">
      {/* Ambient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-transparent opacity-50" />
      
      <div className="relative space-y-6">
        {/* Score Overview */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Brain className="w-6 h-6 text-primary" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Sleep Analysis</h2>
              <div className="flex items-center gap-2 text-sm">
                <span className={scoreDiff >= 0 ? 'text-success' : 'text-destructive'}>
                  {scoreDiff >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                </span>
                <span className="text-muted-foreground">
                  {Math.abs(scoreDiff)}% {scoreDiff >= 0 ? 'improvement' : 'decrease'} from yesterday
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{sleepScore}%</div>
            <div className="text-sm text-muted-foreground">Sleep Score</div>
          </div>
        </div>

        {/* Key Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {activeInsights.map((insight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-lg space-y-2 ${
                insight.type === 'success' 
                  ? 'bg-success/10 border border-success/20' 
                  : insight.type === 'warning'
                  ? 'bg-warning/10 border border-warning/20'
                  : 'bg-muted/30 border border-border/50'
              }`}
            >
              <div className="flex items-center gap-2">
                {insight.type === 'success' ? (
                  <TrendingUp className={`w-4 h-4 ${insight.type === 'success' ? 'text-success' : 'text-warning'}`} />
                ) : insight.type === 'warning' ? (
                  <AlertTriangle className="w-4 h-4 text-warning" />
                ) : (
                  <Moon className="w-4 h-4 text-primary" />
                )}
                <span className="font-medium text-sm">{insight.title}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {insight.description}
              </p>
              <div className="text-xs font-medium">
                Impact: <span className="text-primary">{insight.impact}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}